import { ReactNode, useEffect, useState, useRef } from 'react';
import { Sidebar, SidebarRef } from '../sidebar';
import { Box, useMediaQuery } from '@mui/material';
import { SubscriptionDialogProvider } from '../../context/subscriptionDialogContext';
import { usePageTracking } from '../../hooks';

interface LayoutProps {
  children: ReactNode;
}

const Layout = ({ children }: LayoutProps) => {
  // Track page views on route changes
  usePageTracking();

  const isMobile = useMediaQuery('(max-width:960px)');
  const sidebarRef = useRef<SidebarRef>(null);

  // Initialize sidebar state from localStorage or default based on screen size
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    const savedState = localStorage.getItem('sidebarOpen');
    if (savedState !== null) {
      return JSON.parse(savedState);
    }
    return !isMobile; // Default to open on desktop, closed on mobile
  });

  useEffect(() => {
    // Only auto-close on mobile if user hasn't explicitly set a preference
    const savedState = localStorage.getItem('sidebarOpen');
    if (savedState === null && isMobile) {
      setSidebarOpen(false);
    }
  }, [isMobile]);

  // Listen for openTenantSettings events from child components
  useEffect(() => {
    const handleOpenSettingsEvent = (event: CustomEvent<{ tab?: number }>) => {
      handleOpenTenantSettings(event.detail?.tab);
    };

    window.addEventListener('openTenantSettings', handleOpenSettingsEvent as EventListener);
    return () => {
      window.removeEventListener('openTenantSettings', handleOpenSettingsEvent as EventListener);
    };
  }, []);

  const toggleSidebar = () => {
    const newState = !sidebarOpen;
    setSidebarOpen(newState);
    localStorage.setItem('sidebarOpen', JSON.stringify(newState));
  };

  const handleOpenTenantSettings = (tab?: number) => {
    sidebarRef.current?.openTenantSettings(tab);
  };

  return (
    <SubscriptionDialogProvider onOpenTenantSettings={handleOpenTenantSettings}>
      <Box sx={styles.container}>
        <Sidebar ref={sidebarRef} sidebarOpen={sidebarOpen} onToggleSidebar={toggleSidebar} />
        <Box sx={styles.mainContent}>
          {children}
        </Box>
      </Box>
    </SubscriptionDialogProvider>
  );
};

export default Layout;

const styles = {
  container: {
    display: 'flex',
    height: '100vh',
    bgcolor: 'background.white',
  },
  mainContent: {
    flex: 1,
    px: { xs: 2, sm: 2, md: 3 },
    py: { xs: 1, sm: 1, md: 2 },
    bgcolor: 'background.white',
    width: '100%',
    overflowX: 'auto',
    overflowY: 'auto',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&::-webkit-scrollbar': {
      width: '8px',
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: 'background.lightGrey',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: 'background.scrollbar',
      borderRadius: '100px',
      '&:hover': {
        backgroundColor: 'border.medium',
      },
    },
  },
};
